import { SchedulingDto } from "../dtos/schedulingDto";
import { Scheduling } from "../models/schedule";
import ApiService from "../services/api";

export const fetchScheduling = async (schedulingId: string): Promise<Scheduling | undefined> => {
  const apiService = new ApiService();
  
  try {
      const response = await apiService.get<any>(`/schedules/checkups/${schedulingId}`);
      if (response.status === 200) {
          return SchedulingDto(response.data);
      } else return undefined;
  } catch (error) {
      console.error(error);
      return undefined;
  }
}

export const fetchDeletedSchedule = async (id: string): Promise<any[]> => {
  const apiService = new ApiService();

  try {
    const response = await apiService.post<any[]>(
      `/schedules/checkups/cancel/${id}`,
      {}
    );

    if (response.status === 200) {
      return response.data;
    } else {
      console.warn("Unexpected status code:", response.status);
      return [];
    }
  } catch (error: any) {
    console.error(
      "Error fetching deleted schedule list:",
      error.response?.data || error.message || error
    );
    return [];
  }
};

export const fetchSchedulings = async (): Promise<Scheduling[]> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.get<any[]>(
      `/schedules/checkups/schedules-by-user`
    );
    if (response.status === 200) {
      return response.data.map(SchedulingDto);
    } else {
      console.warn("Unexpected status code:", response.status);
      return [];
    }
  } catch (error) {
    console.error("Error fetching schedule list:", error);
    return [];
  }
};

export const fetchSchedulingByChassisList = async (chassisList: string[]): Promise<Scheduling[]> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.post<any[]>(
      `/schedules/checkups/by-vehicle-chassis`,
      { chassisList: chassisList.filter(el => el) }
    );
    if (response.status === 200) {
      return response.data.map(SchedulingDto);
    } else {
      console.warn("Unexpected status code:", response.status);
      return [];
    }
  } catch (error) {
    console.error("Error fetching schedule list:", error);
    return [];
  }
};

export interface CheckupExistsAppointment {
  state: string;
  appointmentDate: string;
  types: string[];
  dealership: {
    dn: string;
    name: string;
  };
  creatorUserName: string;
  createdAt: string;
}

export interface CheckupExistsByChassisResponse {
  hasActive: boolean;
  appointment?: CheckupExistsAppointment;
}

export const fetchCheckupExistsByChassis = async (
  chassis: string
): Promise<CheckupExistsByChassisResponse> => {
  const apiService = new ApiService();
  const response = await apiService.get<CheckupExistsByChassisResponse>(
    `/schedules/checkups/exists/${encodeURIComponent(chassis)}`
  );
  if (response.status !== 200) {
    throw new Error(`Unexpected status code: ${response.status}`);
  }
  return response.data;
};

export const fetchFleetSchedulings = async (lastKey?: string | null, sortDirection?: string, queryChassis?: string, date?: string, status?: string): Promise<{lastKey: string | null, items: Scheduling[]}> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.get<any>(
      `/schedules/checkups/all?limit=20&lastKey=${lastKey || ''}&sortDirection=${sortDirection || "asc"}&queryChassis=${queryChassis || ""}&date=${date || ""}&state=${status || ""}`
    );
    if (response.status === 200) {
      return {
        lastKey: response.data.lastKey, 
        items: response.data.items
          .map((item: any) => {
            try {
              return SchedulingDto(item);
            } catch (dtoError) {
              console.error("Error mapping scheduling item:", dtoError, item);
              return null;
            }
          })
          .filter((item: any) => item !== null)
      };
    } else {
      console.warn("Unexpected status code:", response.status);
      return {lastKey: null, items: []};
    }
  } catch (error) {
    console.error("Error fetching schedule list:", error);
    return {lastKey: null, items: []} ;
  }
}

export const fetchConsultantSchedulings = async (lastKey?: string | null, sortDirection?: string, queryChassis?: string, date?: string, status?: string): Promise<{lastKey: string | null, items: Scheduling[]}> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.get<any>(
      `/schedules/checkups/by-consultant?limit=20&lastKey=${lastKey || ''}&sortDirection=${sortDirection || "asc"}&queryChassis=${queryChassis || ""}&date=${date || ""}&state=${status || ""}`
    );
    if (response.status === 200) {
      return {
        lastKey: response.data.lastKey, 
        items: response.data.items
          .map((item: any) => {
            try {
              return SchedulingDto(item);
            } catch (dtoError) {
              console.error("Error mapping scheduling item:", dtoError, item);
              return null;
            }
          })
          .filter((item: any) => item !== null)
      };
    } else {
      console.warn("Unexpected status code:", response.status);
      return {lastKey: null, items: []};
    }
  } catch (error) {
    console.error("Error fetching schedule list:", error);
    return {lastKey: null, items: []};
  }
};

export const fetchSchedulingsByMonthAndYear = async (month: number, year: number): Promise<Scheduling[]> => {
  const apiService = new ApiService();
  const query = `month=${month}&year=${year}`
  try {
      const response = await apiService.get<any[]>(`/schedules/checkups?${query}`);
      if (response.status === 200) {
          return response.data.map(SchedulingDto);
      } else return [];
  } catch (error) {
      console.error(error);
      return [];
  }
}

export const fetchConsultantSchedulingsByMonthAndYear = async (
  month: number,
  year: number
): Promise<Scheduling[]> => {
  const apiService = new ApiService();
  const query = `month=${month}&year=${year}`;
  try {
    const response = await apiService.get<any[]>(
      `/schedules/checkups/consultant?${query}`
    );
    if (response.status === 200) {
      return response.data.map(SchedulingDto);
    } else return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const fetchUnviewedSchedulings = async (): Promise<Scheduling[]> => {
  const apiService = new ApiService();
  try {
    const response = await apiService.get<any[]>(
      `/schedules/checkups/schedules-by-consultant?wasViewed=false`
    );
    if (response.status === 200) {
      return response.data.map(SchedulingDto);
    } else return [];
  } catch (error) {
    console.error(error);
    return [];
  }
}